import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import SampleBranding from '../../../types/sample-vehicle-model/SampleBranding';
import SampleVehicleModelAvailabilityWriteDTO
    from '../../../types/sample-vehicle-model/SampleVehicleModelAvailabilityWriteDTO';
import Page from '../../../types/shared/Page';
import VehicleModel from '../../../types/vehicle-model/VehicleModel';
import VehicleModelWriteDTO from '../../../types/vehicle-model/VehicleModelWriteDTO';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class VehicleModelService {

    private RESOURCE: string = 'vehicle-model';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the vehicle models from the backend.
     *
     * @param search The search query to filter the vehicle models by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @param excludeSampleIds The IDs of the samples to exclude from the search.
     * @return The vehicle models.
     */
    public async getVehicleModels(search?: string, page: number = 0, size: number = 10, excludeSampleIds?: number[],
    ): Promise<Page<VehicleModel>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }
        if (excludeSampleIds) {
            params = params.set('excludeSampleIds', excludeSampleIds.join(','));
        }

        return await this.baseService.get<Page<VehicleModel>>(this.RESOURCE, params);
    }

    /**
     * Fetches all vehicle models from the backend that are associated with a sample.
     *
     * @param sampleId The ID of the sample to fetch the vehicle models for.
     * @return The vehicle models.
     */
    public async getVehicleModelsBySampleId(sampleId: number): Promise<VehicleModel[]> {
        return await this.baseService.get<VehicleModel[]>(this.RESOURCE + '/sample/' + sampleId);
    }

    /**
     * Fetches a vehicle model from the backend.
     *
     * @param id The ID of the vehicle model to fetch.
     * @return The vehicle model.
     */
    public async getVehicleModel(id: number): Promise<VehicleModel> {
        return await this.baseService.get<VehicleModel>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a vehicle model in the backend.
     *
     * @param vehicleModel The vehicle model to create.
     * @return The response from the request.
     */
    public async createVehicleModel(vehicleModel: VehicleModelWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, vehicleModel);
    }

    /**
     * Updates a vehicle model in the backend.
     *
     * @param vehicleModel The vehicle model to update.
     * @return The response from the request.
     */
    public async updateVehicleModel(vehicleModel: VehicleModelWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + vehicleModel.id, vehicleModel);
    }

    /**
     * Deletes a vehicle model from the backend.
     *
     * @param id The ID of the vehicle model to delete.
     * @return The response from the request.
     */
    public async deleteVehicleModel(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }

    /**
     * Removes a sample from a vehicle model.
     *
     * @param id The ID of the vehicle model to remove the sample from.
     * @param sampleId The ID of the sample to remove
     * @return The response from the backend.
     */
    public async removeSampleFromVehicleModel(id: number, sampleId: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id + '/samples/' + sampleId);
    }

    /**
     * Adds vehicle models to a sample.
     *
     * @param sampleId The ID of the sample to add the vehicle models to.
     * @param vehicleModelIds The IDs of the vehicle models to add.
     * @return The response from the backend.
     */
    public async addVehicleModelsToSample(sampleId: number, vehicleModelIds: number[]) {
        return this.baseService.post<void>(this.RESOURCE + '/add-to-sample/' + sampleId, vehicleModelIds);
    }

    /**
     * Changes the availability of a sample for a vehicle model.
     *
     * @param vehicleModelId The ID of the vehicle model to change the availability for.
     * @param sampleId The ID of the sample to change the availability for.
     * @param availability The availability to set.
     * @return The response from the backend.
     */
    public async changeSampleAvailability(vehicleModelId: number, sampleId: number,
                                          availability: SampleVehicleModelAvailabilityWriteDTO) {
        return this.baseService.put<void>(
            this.RESOURCE + '/' + vehicleModelId + '/change-availability/' + sampleId, availability);
    }

    /**
     * Changes the sales codes of a sample for a vehicle model.
     *
     * @param vehicleModelId The ID of the vehicle model to change the sales codes for.
     * @param sampleId The ID of the sample to change the sales codes for.
     * @param salesCodes The sales codes to set.
     */
    public async changeSampleSalesCodes(vehicleModelId: number, sampleId: number, salesCodes: string[]) {
        return this.baseService.put<void>(
            this.RESOURCE + '/' + vehicleModelId + '/sales-codes/' + sampleId, salesCodes);
    }

    /**
     * Changes the branding of a sample for a vehicle model.
     *
     * @param vehicleModelId The ID of the vehicle model to change the branding for.
     * @param sampleId The ID of the sample to change the branding for.
     * @param sampleBranding The sample branding to set.
     */
    public async changeSampleBranding(vehicleModelId: number, sampleId: number, sampleBranding: SampleBranding) {
        let httpParams = new HttpParams().set('sampleBranding', sampleBranding);
        return this.baseService.put<void>(
            this.RESOURCE + '/' + vehicleModelId + '/branding/' + sampleId, {}, httpParams);
    }
}
